import React, { Fragment } from 'react';
import { reduxForm, Field } from 'redux-form';
import { FieldEnum } from '../../../common/FieldEnum';
import {
  renderLargeField,
  isValidEmail,
  FieldWithExtractedReduxActions,
} from '../../../common/FormHelper';
import { Button, Checkbox, Grid } from 'semantic-ui-react';
import * as _ from 'lodash';
import { RESET_PASSWORD_FORM_NAME } from '../../../common/FormEnum';
import BaseForm from 'Components/forms/BaseForm';
import { RESET_PASSWORD_STEP } from '../helpers/constants';
import withKeyboard from 'Common/Components/Keyboard/withKeyboard';
import withReduxFormBlur from 'Common/Components/Keyboard/withReduxFormBlur';

const LargeFieldWithKeyboard = FieldWithExtractedReduxActions(
  withReduxFormBlur(withKeyboard(renderLargeField))
);

class ResetPassword extends BaseForm {
  constructor(props) {
    super(props);

    this.state = { settingsDisplay: false };
    this.emailInputRefs = [React.createRef(), React.createRef()];
    this.passwordInputRefs = [React.createRef(), React.createRef()];
    this.activeInputIndex = 0;
  }

  settings() {
    const { ui } = this.props;

    return (
      <div className="login--settings">
        <div className="settings--keyboard">
          <Checkbox
            label="Display Touch Screen Keyboard?"
            checked={ui.keyboardActive}
            onChange={(e, data) => {
              this.keyboardUseToggle();
            }}
          />
        </div>
      </div>
    );
  }

  onKeyboardInputFocus = index => e => {
    this.activeInputIndex = index;
  };

  onKeyboardInputTab = () => {
    const { resetPasswordStep } = this.props;
    const nextInputIndex = this.activeInputIndex + 1;
    let inputRefs = null;

    switch (resetPasswordStep) {
      case RESET_PASSWORD_STEP.Step_1_Before_Email:
        inputRefs = this.emailInputRefs;
        break;
      case RESET_PASSWORD_STEP.Step_2_After_Emai:
        inputRefs = this.passwordInputRefs;
        break;
      default:
    }

    if (inputRefs) {
      if (nextInputIndex !== inputRefs.length) {
        const nextInput = inputRefs[nextInputIndex].current.wrappedInstance;
        nextInput.focus();
      }
    }
  };

  getFields = () => {
    switch (this.props.resetPasswordStep) {
      case RESET_PASSWORD_STEP.Step_1_Before_Email: {
        return (
          <Fragment>
            <div>
              <Field
                maxLength={25}
                name={FieldEnum.DistrictId}
                component={LargeFieldWithKeyboard}
                label="District Code"
                autoComplete={'off'}
                forwardRef={this.emailInputRefs[0]}
                onFocus={this.onKeyboardInputFocus(0)}
                onTabPress={this.onKeyboardInputTab}
                useMargin
              />
            </div>
            <div style={{ marginBottom: '2rem' }}>
              <Field
                name={FieldEnum.Email}
                component={LargeFieldWithKeyboard}
                label="Email Address"
                autoComplete={'off'}
                padBottom={true}
                forwardRef={this.emailInputRefs[1]}
                onFocus={this.onKeyboardInputFocus(1)}
                onTabPress={this.onKeyboardInputTab}
                useMargin
              />
            </div>
          </Fragment>
        );
      }

      case RESET_PASSWORD_STEP.Step_2_After_Email: {
        return (
          <Fragment>
            <div>
              <Field
                name={FieldEnum.Password}
                component={LargeFieldWithKeyboard}
                type="password"
                label="New Password"
                attr={{
                  autoComplete: 'new-password',
                }}
                forwardRef={this.passwordInputRefs[0]}
                onFocus={this.onKeyboardInputFocus(0)}
                onTabPress={this.onKeyboardInputTab}
                useMargin
              />
            </div>
            <div style={{ marginBottom: '2rem' }}>
              <Field
                name={FieldEnum.ConfirmPassword}
                component={LargeFieldWithKeyboard}
                type="password"
                label="Confirm New Password"
                attr={{
                  autoComplete: 'new-password',
                }}
                padBottom={true}
                forwardRef={this.passwordInputRefs[1]}
                onFocus={this.onKeyboardInputFocus(1)}
                onTabPress={this.onKeyboardInputTab}
                useMargin
              />
            </div>
          </Fragment>
        );
      }
      default:
        return null;
    }
  };

  render() {
    const { handleSubmit, invalid } = this.props,
      { settingsDisplay } = this.state;
    return (
      <div>
        <form onSubmit={handleSubmit}>
          {this.getFields()}
          <Grid>
            <Grid.Row>
              <Grid.Column width={2}>
                <Button
                  primary
                  floated="left"
                  type="button"
                  icon="settings"
                  size="tiny"
                  className="login--settings-trigger"
                  onClick={event => {
                    event.preventDefault();
                    this.setState({
                      settingsDisplay: !this.state.settingsDisplay,
                    });
                  }}
                />
              </Grid.Column>
              <Grid.Column width={7}>
                <Button
                  primary
                  type="button"
                  onClick={() => {
                    this.props.setResetPasswordStep(null);
                    this.props.history.replace('/');
                  }}
                >
                  Back To Login
                </Button>
              </Grid.Column>
              <Grid.Column width={7}>
                <Button
                  primary
                  type="submit"
                  disabled={this.props.resetPasswordLoading || invalid}
                  loading={this.props.resetPasswordLoading}
                >
                  Reset Password
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
          {!settingsDisplay ? null : this.settings()}
        </form>
      </div>
    );
  }
}

const validate = values => {
  const errors = {};
  if (
    !values[FieldEnum.DistrictId] ||
    _.trim(values[FieldEnum.DistrictId]).length < 1
  ) {
    errors[FieldEnum.DistrictId] = 'District Code Required';
  }

  if (!values[FieldEnum.Email] || _.trim(values[FieldEnum.Email]).length < 1) {
    errors[FieldEnum.Email] = 'Email Required';
  }

  if (
    values[FieldEnum.Email] &&
    _.trim(values[FieldEnum.Email]).length > 0 &&
    !isValidEmail(_.trim(values[FieldEnum.Email]))
  ) {
    errors[FieldEnum.Email] = 'Valid Email Required';
  }

  if (!values[FieldEnum.Password] || values[FieldEnum.Password].length < 6) {
    errors[FieldEnum.Password] =
      'Password is required and must be 6 characters.';
  }

  if (
    !values[FieldEnum.ConfirmPassword] ||
    values[FieldEnum.ConfirmPassword].length < 6
  ) {
    errors[FieldEnum.ConfirmPassword] =
      'Password is required and must be 6 characters.';
  }

  if (
    values[FieldEnum.ConfirmPassword] &&
    values[FieldEnum.Password] &&
    values[FieldEnum.ConfirmPassword] !== values[FieldEnum.Password]
  ) {
    errors[FieldEnum.ConfirmPassword] = 'Passwords must match.';
  }

  return errors;
};

export default reduxForm({
  validate,
  enableReinitialize: true,
  form: RESET_PASSWORD_FORM_NAME,
})(ResetPassword);



// WEBPACK FOOTER //
// ./src/pages/login/components/ResetPasswordForm.js